import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useRevalidator } from "react-router";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  AcademicCapIcon,
  Bars3Icon,
  BookOpenIcon,
  CalendarIcon,
  ChevronUpDownIcon,
  CogIcon,
  DocumentTextIcon,
  HomeIcon,
  SparklesIcon,
  UserCircleIcon,
  UserGroupIcon,
  GlobeEuropeAfricaIcon,
  WrenchScrewdriverIcon,
  GiftIcon,
} from "@heroicons/react/24/outline";
import { Logo } from "~/components/icons";
import { SpaceLogo } from "~/components/containers/space-logo";
import {
  EngineIntroPanel,
  engineIntroAudience,
  type EngineIntroChoice,
} from "~/components/containers/engine-intro";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
  cn,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sheet,
  SheetContent,
  SheetTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  toast,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@mindstonehq/ui";
import { BugIcon, HelpCircleIcon, LogOutIcon, MessageCircleWarningIcon } from "lucide-react";
import { graphql } from "~/graphql/codegen";
import { useMutation } from "@apollo/client/react";
import { SpaceMetadataFragment, CurrentUserFragment } from "~/graphql/codegen/graphql";
import { useConfig } from "~/hooks/webconfig";
import { useCustomerSupport } from "~/hooks/customer-support";
import {
  useHasPublishedEvents,
  useIsAuthenticated,
  useMarkEngineIntroSeen,
  useOrganisationSwitch,
  useProfile,
  useUser,
} from "~/hooks/context-hook";
import {
  isEventsNavAvailable,
  isOrganisersNavAvailable,
  isEnrolledInAnySpace,
  isProgramsNavAvailable,
} from "~/components/containers/nav-availability";
import Notifications from "~/components/notifications/notifications";
import { isFrontend } from "~/utils";
import { displayNameOf } from "~/utils/display-name";
import { spaceUrl } from "~/utils/space-url";
import { isAdminChampionOrCoach } from "~/utils/mindspace-permission";
import { PointsAndLevelDisplay } from "~/components/points/points-display";
import { ArrowUpRightIcon, ChartBarIcon, UsersIcon } from "@heroicons/react/24/outline";
import { useTransformationDashboard } from "~/hooks/use-transformation-dashboard";
import { organisationNav, parseSwitchDay } from "~/utils/organisation-switch";
import { MindspaceRole } from "~/graphql/codegen/graphql";
import NPSSurveyContainer from "~/components/nps/nps-survey-container";
import metrics from "~/utils/metrics";
import copy from "copy-to-clipboard";
import { CopyIcon } from "~/components/icons";
import { DiscountCodeDialog } from "~/components/discount-code-dialog";
import { ReportBugDialog } from "~/components/report-bug/report-bug-dialog";
import { canShowControlledBugReport } from "~/components/report-bug/report-bug-visibility";

const MindspaceJoinDocument = graphql(`
  mutation MindspaceJoin($mindspaceId: ID!) {
    mindspaceJoin(mindspaceId: $mindspaceId)
  }
`);

function useMindspaceJoinMutation() {
  return useMutation(MindspaceJoinDocument);
}

// WCAG relative luminance for a 6-digit hex colour (0 = black, 1 = white).
function colorLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return (
    0.2126 * (r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)) +
    0.7152 * (g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)) +
    0.0722 * (b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4))
  );
}

// Returns black or white for the best contrast against a 6-digit hex background.
function accessibleTextColor(hex: string): "#000000" | "#ffffff" {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return "#ffffff";
  return colorLuminance(hex) > 0.179 ? "#000000" : "#ffffff";
}

// Darken a 6-digit hex colour by mixing it with black. `amount` is 0–1.
function darkenColor(hex: string, amount: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  const r = Math.max(0, Math.round(parseInt(hex.slice(1, 3), 16) * (1 - amount)));
  const g = Math.max(0, Math.round(parseInt(hex.slice(3, 5), 16) * (1 - amount)));
  const b = Math.max(0, Math.round(parseInt(hex.slice(5, 7), 16) * (1 - amount)));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

// Lighten a 6-digit hex colour by mixing it with white. `amount` is 0–1.
function lightenColor(hex: string, amount: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  const r = Math.min(255, Math.round(parseInt(hex.slice(1, 3), 16) + (255 - parseInt(hex.slice(1, 3), 16)) * amount));
  const g = Math.min(255, Math.round(parseInt(hex.slice(3, 5), 16) + (255 - parseInt(hex.slice(3, 5), 16)) * amount));
  const b = Math.min(255, Math.round(parseInt(hex.slice(5, 7), 16) + (255 - parseInt(hex.slice(5, 7), 16)) * amount));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

// Blend two 6-digit hex colours. `amount` is how much of `tint` to mix into
// `base` (0–1). Used to subtly tint the dark theme with partner colours.
function blendColors(base: string, tint: string, amount: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(base) || !/^#[0-9a-fA-F]{6}$/.test(tint)) return base;
  const baseR = parseInt(base.slice(1, 3), 16);
  const baseG = parseInt(base.slice(3, 5), 16);
  const baseB = parseInt(base.slice(5, 7), 16);
  const tintR = parseInt(tint.slice(1, 3), 16);
  const tintG = parseInt(tint.slice(3, 5), 16);
  const tintB = parseInt(tint.slice(5, 7), 16);
  const r = Math.round(baseR + (tintR - baseR) * amount);
  const g = Math.round(baseG + (tintG - baseG) * amount);
  const b = Math.round(baseB + (tintB - baseB) * amount);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

// Small hook that follows the document 'dark' class so partner colour
// overrides can be adapted when the user toggles light/dark mode.
function useDarkMode(): boolean {
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const root = document.documentElement;
    setIsDark(root.classList.contains("dark"));
    const observer = new MutationObserver(() => {
      setIsDark(root.classList.contains("dark"));
    });
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

const DEFAULT_SECONDARY_COLOR = "#eef2ff";

export interface NavigationItem {
  name: string;
  label?: MessageDescriptor;
  href?: string;
  icon: any;
  current?: (path: string) => boolean;
  available: (space: SpaceMetadataFragment, user?: CurrentUserFragment | null) => boolean;
  /** Runs on activation; `popupShown` when the click opened the item's intro instead. */
  action?: (entry: { popupShown: boolean }) => any;
  separator?: boolean;
  /**
   * Full page load instead of client-side routing — for a destination another
   * app serves (e.g. the Transformation dashboard at /transformation on this
   * same origin), which this router would otherwise try to match.
   */
  reloadDocument?: boolean;
  /** Opens in a new tab, with the new-tab glyph; say so in `spokenLabel`. */
  newTab?: boolean;
  /** A short visual marker after the label ("Moved"); say it in `spokenLabel`. */
  badge?: MessageDescriptor;
  /** The item's accessible name when it differs from the label. */
  spokenLabel?: MessageDescriptor;
  /** A one-time panel the next plain click opens instead of the link (FOX-6291 A5). */
  intro?: NavIntro;
}

/** Where a sidebar item is drawn: the desktop rail or the phone drawer. */
type NavPlace = "rail" | "drawer";

type NavIntro = {
  /** The next plain click opens the panel instead of following the link. */
  pending: boolean;
  openAt: NavPlace | null;
  show: (at: NavPlace) => void;
  /** The item, wrapped in the panel (element id `panelId`) anchored to it. */
  wrap: (at: NavPlace, item: React.ReactNode, panelId: string) => React.ReactNode;
};

const MarkOrganizationMovedSeenDocument = graphql(`
  mutation MarkOrganizationMovedSeen {
    updateUserStateStore(organizationMovedSeen: true) {
      organizationMovedSeen
    }
  }
`);

const MarkEngineIntroSeenDocument = graphql(`
  mutation MarkEngineIntroSeen {
    updateUserStateStore(transformationEngineIntroSeen: true) {
      transformationEngineIntroSeen
    }
  }
`);

export default function PageLayout({
  spaces,
  currentSpace,
  url,
  children,
}: React.PropsWithChildren<{
  spaces: SpaceMetadataFragment[];
  url: string;
  currentSpace: SpaceMetadataFragment;
}>) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [container, setContainer] = useState<any>(null);
  const location = useLocation();
  const [joinMindspaceMutation] = useMindspaceJoinMutation();
  const user = useUser();
  const profile = useProfile();
  const authenticated = useIsAuthenticated();
  const config = useConfig();
  const support = useCustomerSupport();
  // "Does this Mindspace run events at all?" — threaded as a scalar from the root
  // loader (see root.tsx / MainContext). Gates the Events tab and, additionally,
  // the Organisers item.
  const hasPublishedEvents = useHasPublishedEvents();
  const navigate = useNavigate();
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [reportBugDialogOpen, setReportBugDialogOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const referralCode = user?.referrals?.[0]?.referralCode;
  const { t } = useLingui();
  const isDarkMode = useDarkMode();
  const transformationDashboard = useTransformationDashboard(currentSpace?.id, authenticated);
  // FOX-6291 Part A: the move from the Organisation page to the Transformation
  // Engine (utils/organisation-switch.ts organisationNav).
  const organisationSwitch = useOrganisationSwitch();
  const [movedItemOpened, setMovedItemOpened] = useState(false);
  const [markOrganizationMovedSeen] = useMutation(MarkOrganizationMovedSeenDocument);
  const [markEngineIntroSeen] = useMutation(MarkEngineIntroSeenDocument);
  // A5: where the intro is open is held here, not in the item, so it survives
  // the phone drawer unmounting its items; "seen" is root's (MainContext), so
  // the home card reads the same answer.
  const noteEngineIntroSeen = useMarkEngineIntroSeen();
  const [engineIntroAt, setEngineIntroAt] = useState<NavPlace | null>(null);
  const engineIntroDone = useRef(false);
  const viewerPermission = currentSpace?.currentUserPermission;
  const spaceCreatedAt = organisationSwitch.spaceCreatedAt
    ? new Date(organisationSwitch.spaceCreatedAt)
    : null;
  const switchNav = organisationNav({
    phase: organisationSwitch.phase,
    switchDay: parseSwitchDay(organisationSwitch.switchDay),
    spaceCreatedAt: spaceCreatedAt && !Number.isNaN(spaceCreatedAt.getTime()) ? spaceCreatedAt : null,
    seen: organisationSwitch.movedSeen || movedItemOpened,
    permission: viewerPermission,
    dashboard: transformationDashboard,
    legacyOrganization:
      !!user &&
      isAdminChampionOrCoach(viewerPermission) &&
      !!currentSpace?.modules.programs,
  });

  const handleCopy = () => {
    if (referralCode) {
      copy(referralCode);
      toast.success(t(msg`Discount code copied to clipboard`));
    }
  };

  // Hide navigation on program day
  // I wonder when this will break...
  const hideNavigation = location.pathname.includes("programday_");

  // Visible cohorts on any of the learner's spaces, never `total` (FOX-6893).
  const enrolledAnywhere = isEnrolledInAnySpace(spaces);

  const isOnProgram = currentSpace.modules.programs && !!currentSpace.programs?.enrolled.total && !!user;

  // Partner brand accent: applied ONLY as a CSS custom-property value (never
  // injected as markup), and only when it's a valid 6-digit hex. When unset the
  // accent spots fall back to today's violet, so non-partner spaces are unchanged.
  const brandColor = currentSpace.partner?.brandColor ?? "";
  const primaryColor = currentSpace.partner?.primaryColor ?? "";
  const secondaryColor = currentSpace.partner?.secondaryColor ?? "";
  // When a partner sets a valid brand colour we paint the app chrome (top bar,
  // mobile drawer, desktop sidebar) with it instead of the default dark
  // surface. The text colour is computed automatically for accessibility.
  const onBrand = /^#[0-9a-fA-F]{6}$/.test(brandColor);
  const brandTextColor = onBrand ? accessibleTextColor(brandColor) : undefined;
  // Hover state and borders should feel native to the chosen brand colour, not
  // generic grey/black. We derive them from the brand background itself so they
  // stay harmonious with the sidebar palette.
  const brandHoverBg = onBrand ? darkenColor(brandColor, 0.2) : undefined;
  // Nav-item hover is intentionally subtler than the space-selector / popover
  // hover so the sidebar feels light, not painted.
  const brandNavHoverBg = onBrand ? darkenColor(brandColor, 0.1) : undefined;
  const brandBorderColor = onBrand ? darkenColor(brandColor, 0.25) : undefined;
  const brandSurfaceStyle: React.CSSProperties | undefined = onBrand
    ? ({
        backgroundColor: brandColor,
        color: brandTextColor,
        "--brand-hover-bg": brandHoverBg,
        "--brand-nav-hover-bg": brandNavHoverBg,
        "--brand-border-color": brandBorderColor,
      } as React.CSSProperties)
    : undefined;

  // Apply partner primary/secondary colours to the design-system CSS variables
  // when valid. Empty values fall back to the default Mindstone tokens.
  // In dark mode we adapt the colours so buttons stay visible and surfaces
  // remain harmonious — a very dark primary on a dark background would
  // disappear, and a light secondary on a dark page would blind the user.
  useEffect(() => {
    const root = document.documentElement;

    const effectivePrimary = (() => {
      if (!/^#[0-9a-fA-F]{6}$/.test(primaryColor)) return null;
      if (!isDarkMode) return primaryColor;
      // If the primary is too dark to be visible on a dark surface, lighten it.
      return colorLuminance(primaryColor) < 0.4 ? lightenColor(primaryColor, 0.4) : primaryColor;
    })();

    if (effectivePrimary) {
      root.style.setProperty("--color-primary", effectivePrimary);
      root.style.setProperty("--color-primary-hover", effectivePrimary);
      root.style.setProperty("--color-primary-foreground", accessibleTextColor(effectivePrimary));
    } else {
      root.style.removeProperty("--color-primary");
      root.style.removeProperty("--color-primary-hover");
      root.style.removeProperty("--color-primary-foreground");
    }

    const effectiveSecondary = (() => {
      const base = /^#[0-9a-fA-F]{6}$/.test(secondaryColor) ? secondaryColor : DEFAULT_SECONDARY_COLOR;
      if (!isDarkMode) return base;
      // Darken the secondary colour so it works as a dark-mode accent/surface.
      return darkenColor(base, 0.55);
    })();

    root.style.setProperty("--color-secondary", effectiveSecondary);
    root.style.setProperty("--color-secondary-hover", effectiveSecondary);
    root.style.setProperty("--color-secondary-foreground", accessibleTextColor(effectiveSecondary));

    // In dark mode, subtly tint the core dark palette with the partner's
    // secondary (or brand) colour so the main content area feels connected to
    // the light sidebar instead of jarring.
    const isPartnerBranded = onBrand || /^#[0-9a-fA-F]{6}$/.test(primaryColor) || /^#[0-9a-fA-F]{6}$/.test(secondaryColor);
    if (isDarkMode && isPartnerBranded) {
      const tint = /^#[0-9a-fA-F]{6}$/.test(secondaryColor) ? secondaryColor : brandColor;
      root.style.setProperty("--color-background", blendColors("#0f172a", tint, 0.1));
      root.style.setProperty("--color-card", blendColors("#1e293b", tint, 0.08));
      root.style.setProperty("--color-border", blendColors("#334155", tint, 0.18));
      root.style.setProperty("--color-accent", blendColors("#475569", tint, 0.22));
      root.style.setProperty("--color-muted", blendColors("#334155", tint, 0.12));
      root.style.setProperty("--color-popover", blendColors("#1e293b", tint, 0.08));
    } else {
      root.style.removeProperty("--color-background");
      root.style.removeProperty("--color-card");
      root.style.removeProperty("--color-border");
      root.style.removeProperty("--color-accent");
      root.style.removeProperty("--color-muted");
      root.style.removeProperty("--color-popover");
    }

    return () => {
      root.style.removeProperty("--color-primary");
      root.style.removeProperty("--color-primary-hover");
      root.style.removeProperty("--color-primary-foreground");
      root.style.removeProperty("--color-secondary");
      root.style.removeProperty("--color-secondary-hover");
      root.style.removeProperty("--color-secondary-foreground");
      root.style.removeProperty("--color-background");
      root.style.removeProperty("--color-card");
      root.style.removeProperty("--color-border");
      root.style.removeProperty("--color-accent");
      root.style.removeProperty("--color-muted");
      root.style.removeProperty("--color-popover");
    };
  }, [primaryColor, secondaryColor, isDarkMode, brandColor, onBrand]);

  const home: NavigationItem = {
    name: "Home",
    label: msg`Home`,
    href: "/",
    icon: HomeIcon,
    current: (path: string) => path === "/",
    available: (_, user) => !!user,
  };

  const programs: NavigationItem = {
    name: "Programs",
    label: msg`Programs`,
    href: "/programs",
    icon: AcademicCapIcon,
    current: (path: string) => path.startsWith("/programs"),
    // FOX-6893: also on a space with programs off when the learner is enrolled
    // somewhere; /programs there lists the programmes in their other spaces.
    available: (space) => isProgramsNavAvailable({ programsModuleEnabled: space.modules.programs, enrolledAnywhere }),
  };

  // Today's Organization item, or from the switch day its "Moved" form, which
  // goes once the viewer has opened the moved page from it.
  const movedItem = switchNav.organization === "moved";
  const organization: NavigationItem = {
    name: "Organization",
    label: msg`Organization`,
    href: "/organization",
    icon: UsersIcon,
    current: (path: string) => path.startsWith("/organization"),
    badge: movedItem ? msg`Moved` : undefined,
    spokenLabel: movedItem ? msg`Organization, moved` : undefined,
    action: movedItem
      ? () => {
          setMovedItemOpened(true);
          markOrganizationMovedSeen().catch(() => {
            // The item shows again on the next visit; the page still opens.
          });
          metrics.track("organization_moved_item_clicked", {
            space: currentSpace?.uniqueName,
            role: viewerPermission?.role === MindspaceRole.Admin ? "admin" : "champion",
          });
        }
      : undefined,
    available: () => switchNav.organization !== "none",
  };

  // The company's Transformation dashboard (another app, possibly on this same
  // origin at /transformation), for everyone the engine sends a link: leaders,
  // admins, champions and (plan 261104_learner-dashboard-link) learners.
  // From the switch day it is the Transformation Engine, in a new tab.
  const engineItem = switchNav.engine === "engine";
  // A5: until the viewer dismisses it, the first plain click on the engine
  // item opens a short explanation (engine-intro.tsx) instead of the new tab.
  const engineIntroSeen = organisationSwitch.engineIntroSeen;
  const engineIntroAudienceValue = transformationDashboard
    ? engineIntroAudience(transformationDashboard)
    : "leader";
  const dismissEngineIntro = (choice: EngineIntroChoice) => {
    // Open is followed by the Popover's own close: write and track once.
    if (engineIntroDone.current) return;
    engineIntroDone.current = true;
    noteEngineIntroSeen();
    setEngineIntroAt(null);
    markEngineIntroSeen().catch(() => {
      // It shows again on the next visit; the engine still opens.
    });
    metrics.track("engine_intro_dismissed", {
      choice,
      audience: engineIntroAudienceValue,
      space: currentSpace?.uniqueName,
    });
  };
  // Every way the phone drawer closes (an item, the close button, outside):
  // closing it over the intro is a Not now.
  const closeSidebar = () => {
    if (engineIntroAt === "drawer") dismissEngineIntro("not_now");
    setSidebarOpen(false);
  };
  const engineIntro: NavIntro | undefined =
    engineItem && transformationDashboard
      ? {
          pending: !engineIntroSeen,
          openAt: engineIntroAt,
          show: setEngineIntroAt,
          wrap: (at, item, panelId) => (
            <EngineIntroPanel
              id={panelId}
              open={engineIntroAt === at}
              href={transformationDashboard.url}
              audience={engineIntroAudienceValue}
              side={at === "drawer" ? "bottom" : "right"}
              onDismiss={dismissEngineIntro}
            >
              {item}
            </EngineIntroPanel>
          ),
        }
      : undefined;
  const transformation: NavigationItem = {
    name: "Transformation",
    label: engineItem ? msg`Transformation Engine` : msg`Transformation`,
    spokenLabel: engineItem ? msg`Transformation Engine (opens in new tab)` : undefined,
    href: transformationDashboard?.url,
    icon: ChartBarIcon,
    newTab: engineItem,
    reloadDocument: !engineItem,
    current: (path: string) => path.startsWith("/transformation"),
    action: ({ popupShown }) =>
      metrics.track("dashboard_entry_clicked", {
        surface: "sidebar",
        audience: transformationDashboard?.audience,
        space: currentSpace?.uniqueName,
        // Before the switch day this is today's Transformation item.
        phase: organisationSwitch.phase,
        // Not yet dismissed the A5 intro, and whether this click opened it.
        first_visit: !engineIntroSeen,
        popup_shown: popupShown,
      }),
    intro: engineIntro,
    available: () => switchNav.engine !== "none",
  };

  const events: NavigationItem = {
    name: "Events",
    label: msg`Events`,
    href: "/events",
    icon: CalendarIcon,
    current: (path: string) => path.startsWith("/events"),
    available: (space) =>
      isEventsNavAvailable({
        eventsModuleEnabled: space.modules.events,
        hasPublishedEvents,
      }),
  };

  const tools: NavigationItem = {
    name: "AI Tools",
    label: msg`AI Tools`,
    href: "/tools",
    icon: WrenchScrewdriverIcon,
    current: (path: string) => path.startsWith("/tools"),
    available: () => true,
  };

  const separatorItem: NavigationItem = {
    name: "separator",
    icon: null,
    available: () => true,
    separator: true,
  } as any;

  const sandbox: NavigationItem = {
    name: "Sandbox",
    label: msg`Sandbox`,
    href: "/sandbox",
    icon: SparklesIcon,
    current: (path: string) => path.startsWith("/sandbox"),
    available: () => true,
  };

  const content: NavigationItem = {
    name: "Content",
    label: msg`Content`,
    href: "/content",
    icon: DocumentTextIcon,
    current: (path: string) => path.startsWith("/content"),
    available: (_, user) => !!user,
  };

  const members: NavigationItem = {
    name: "Members",
    label: msg`Members`,
    href: "/members",
    icon: UserGroupIcon,
    current: (path: string) => path.startsWith("/members"),
    available: (space, user) => {
      const currentUrl = new URL(url);
      const communityUrl = new URL(config.communityUrl);
      const isAdmin = space.currentUserPermission?.role === "admin";
      const isStaff = user?.isStaff ?? false;
      return (
        !!user &&
        (currentUrl.hostname.includes(communityUrl.hostname) || !space.modules.programs || (isAdmin && isStaff))
      );
    },
  };

  const organisers: NavigationItem = {
    name: "Organisers",
    label: msg`Organisers`,
    href: "/organisers",
    icon: GlobeEuropeAfricaIcon,
    current: (path: string) => path.startsWith("/organisers"),
    available: () =>
      isOrganisersNavAvailable({
        currentUrl: url,
        communityUrl: config.communityUrl,
        environment: config.environment,
        hasPublishedEvents,
      }),
  };

  const useCases: NavigationItem = {
    name: "Use Cases",
    label: msg`Use Cases`,
    href: "/use-cases",
    icon: BookOpenIcon,
    current: (path: string) => path.startsWith("/use-cases"),
    available: () => true,
  };

  const reportBug: NavigationItem = {
    name: "Report a bug",
    label: msg`Report a bug`,
    icon: BugIcon,
    action: () => setReportBugDialogOpen(true),
    available: (space, currentUser) =>
      canShowControlledBugReport({
        isStaff: currentUser?.isStaff === true,
        reporterEmail: currentUser?.email,
        mindspaceUniqueName: space.uniqueName,
      }),
  };

  const navigation = [
    home,
    programs,
    organization,
    transformation,
    events,
    content,
    members,
    organisers,
    separatorItem,
    sandbox,
    useCases,
    tools,
    reportBug,
  ];

  const handleJoinSpace = () => {
    joinMindspaceMutation({
      variables: {
        mindspaceId: currentSpace.id,
      },
    })
      .then(() => {
        // Force reload to ensure all space data is refreshed
        window.location.reload();
      })
      .catch(() => {
        toast.error(t(msg`Something went wrong. Please try again later.`));
      });
  };

  return (
    <div className={"flex h-[100dvh] overflow-y-hidden w-screen flex-col md:flex-row"}>
      {!hideNavigation && (
        <>
          {/* Mobile top navigation bar with hamburger menu */}
          <div
            style={brandSurfaceStyle}
            className={cn(
              "sticky dark top-0 w-full z-40 h-16 shrink-0 flex justify-between items-center gap-x-6 bg-card text-foreground px-4 py-4 sm:px-6 md:hidden border-b shadow-sm",
              onBrand ? "border-[var(--brand-border-color)]" : "border-border",
            )}
          >
            <div className="flex flex-row shrink-0 space-x-2 items-center">
              <Sheet
                open={sidebarOpen}
                onOpenChange={(open) => (open ? setSidebarOpen(true) : closeSidebar())}
              >
                <SheetTrigger asChild>
                  <Button variant={"ghost"} className="-m-2.5 p-2.5 xlhidden">
                    <span className="sr-only">
                      <Trans>Open sidebar</Trans>
                    </span>
                    <Bars3Icon className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent style={brandSurfaceStyle} className="dark bg-card text-foreground" side="left">
                  {sidebarOpen && (
                    <div style={brandSurfaceStyle} className="flex bg-card h-full grow flex-col gap-y-5 overflow-y-auto px-4 py-2">
                      <div className="flex mt-3 text-xl font-medium shrink-0">
                        <Link to={"/"}>
                          <SpaceLogo partner={currentSpace.partner} className="h-6" />
                        </Link>
                      </div>
                      <nav className="flex flex-1 flex-col mt-6 grow">
                        <div role="list" className="flex flex-1 flex-col gap-y-7 list-none">
                          <div role="list" className="flex flex-col gap-y-2 list-none">
                            {navigation
                              .filter((x) => x.available(currentSpace, user))
                              .map((item, idx) =>
                                item.separator ? (
                                  <Separator
                                    key={`sep-${idx}`}
                                    className={cn(
                                      "my-2",
                                      onBrand ? "bg-[var(--brand-border-color)]" : "bg-border",
                                    )}
                                  />
                                ) : (
                                  <MenuItem
                                    item={item}
                                    key={item.href || item.name}
                                    closeSidebar={closeSidebar}
                                    onBrand={onBrand}
                                    brandHoverBg={brandHoverBg}
                                    brandTextColor={brandTextColor}
                                  />
                                ),
                              )}
                          </div>
                        </div>
                      </nav>
                      {authenticated && !spaces.find((x) => x.id === currentSpace.id) && (
                        <Card className="light">
                          <CardHeader className="p-3">
                            <CardTitle className="flex flex-row items-center gap-1 text-warning">
                              <MessageCircleWarningIcon className="h-5" />
                              <span>
                                <Trans>Not a member</Trans>
                              </span>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3 pt-0">
                            <p className="text-muted-foreground text-sm">
                              {!!currentSpace.configuration?.public ? (
                                <Trans>
                                  Switch to your existing space below, or{" "}
                                  <a
                                    href="#"
                                    onClick={handleJoinSpace}
                                    className="text-foreground underline hover:text-foreground/80"
                                  >
                                    join {currentSpace.name}
                                  </a>
                                  .
                                </Trans>
                              ) : (
                                <Trans>Switch to your existing space below.</Trans>
                              )}
                            </p>
                          </CardContent>
                        </Card>
                      )}
                      {authenticated && (spaces.length > 1 || !spaces.find((x) => x.id === currentSpace.id)) ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className={cn(
                                "flex w-full items-center gap-x-2 px-3 py-2 typography-detail rounded-md my-4 h-auto",
                                onBrand ? "bg-[var(--brand-hover-bg)]" : "bg-slate-900",
                              )}
                            >
                              <img
                                className="shrink-0 h-4 w-4 rounded-xs object-contain"
                                src={currentSpace.images.thumbnail}
                                alt={currentSpace.name}
                              />
                              <span className="sr-only text-left">
                                <Trans>Change space</Trans>
                              </span>
                              <div className="line-clamp-1 grow w-full text-left">{currentSpace.name}</div>
                              <ChevronUpDownIcon className={"h-8 w-8"} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-56  max-h-[300px] md:max-h-[400px] lg:max-h-[600px] overflow-auto">
                            <DropdownMenuLabel>
                              <Trans>Spaces</Trans>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {spaces.map((space) => {
                              return (
                                <DropdownMenuCheckboxItem
                                  key={space.uniqueName}
                                  checked={space.uniqueName === currentSpace.uniqueName}
                                  className="flex justify-between"
                                  onCheckedChange={() => {
                                    window.location.href = spaceUrl(config.appUrl, currentSpace.uniqueName, space.uniqueName);
                                  }}
                                >
                                  <span> {space.name}</span>
                                  <img
                                    className="h-6 w-6 rounded-xs bg-gray-50 object-contain"
                                    src={space.images.thumbnail}
                                    alt={space.name}
                                  />
                                </DropdownMenuCheckboxItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <>
                          <div className="flex w-full items-center gap-x-2 px-3 py-1.5 typography-detail rounded-md my-4 h-auto">
                            <img
                              className="shrink-0 h-4 w-4 rounded-xs object-contain"
                              src={currentSpace.images.thumbnail}
                              alt={currentSpace.name}
                            />
                            <div className="line-clamp-1 grow w-full text-left">{currentSpace.name}</div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </SheetContent>
              </Sheet>
            </div>
            <div className="text-sm font-semibold leading-6">
              <Link to={"/"}>
                <SpaceLogo partner={currentSpace.partner} className="h-6" variant="bar" />
              </Link>
            </div>

            <div className="flex flex-row shrink-0 space-x-2 items-center">
              {user && (
                <>
                  {(currentSpace?.programs?.enrolled?.total ?? 0) > 0 && <PointsAndLevelDisplay variant="mobile" />}
                  <Notifications container={container} iconClassName={"h-7 w-7"} />
                  <DropdownMenu open={mobileDropdownOpen} onOpenChange={setMobileDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                      <a href="#" className="h-8 w-8" aria-label={t(msg`User profile menu`)}>
                        <span className="sr-only">
                          <Trans>Your profile</Trans>
                        </span>
                        <img
                          className="h-8 w-8 bg-gray-50 rounded-full"
                          src={profile?.profilePictureURL}
                          alt={`${displayNameOf(profile)}'s profile picture`}
                        />
                      </a>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56">
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex items-center space-x-2">
                          {profile?.profilePictureURL ? (
                            <img
                              className="h-8 w-8 bg-gray-50 rounded-full"
                              src={profile?.profilePictureURL}
                              alt={`${displayNameOf(profile)}'s profile picture`}
                            />
                          ) : (
                            <div className="h-8 w-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                              {profile?.name
                                ? profile.name.charAt(0).toUpperCase()
                                : profile?.username?.charAt(0).toUpperCase() || "U"}
                            </div>
                          )}
                          <div className="flex flex-col space-y-1">
                            <p className="text-sm font-medium leading-none">{profile?.name || profile?.username}</p>
                            <p className="text-xs leading-none text-muted-foreground">{profile?.email}</p>
                          </div>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        <DropdownMenuItem onClick={() => navigate(`/profile/${profile?.username}`)}>
                          <UserCircleIcon className="mr-2 h-4 w-4" />
                          <span>
                            <Trans>Profile</Trans>
                          </span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/settings")}>
                          <CogIcon className="mr-2 h-4 w-4" />
                          <span>
                            <Trans>Settings</Trans>
                          </span>
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        {support.available && (
                          <DropdownMenuItem onSelect={support.open}>
                            <HelpCircleIcon className="mr-2 h-4 w-4" />
                            <span>
                              <Trans>Help</Trans>
                            </span>
                          </DropdownMenuItem>
                        )}
                        {referralCode && (
                          <DropdownMenuItem
                            onClick={() => {
                              setMobileDropdownOpen(false);
                              setDiscountDialogOpen(true);
                            }}
                          >
                            <GiftIcon className="mr-2 h-4 w-4" />
                            <span>
                              <Trans>Discount code</Trans>
                            </span>
                            <span className="ml-auto text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                              <Trans>20% off!</Trans>
                            </span>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          metrics.track("logout");
                          navigate("/logout");
                        }}
                      >
                        <LogOutIcon className="mr-2 h-4 w-4" />
                        <span>
                          <Trans>Logout</Trans>
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
              {!user && (
                <>
                  <Button variant={"secondary"} size="sm" asChild>
                    <Link to={`/login?redirectUrl=${location.pathname}`}>
                      <Trans>Login</Trans>
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Sidebar for desktop (has a small and md version) */}
          <div className="dark hidden md:shrink-0 md:inset-y-0 md:z-50 md:flex md:w-20 xl:w-[190px] md:flex-col text-foreground">
          <div
            style={brandSurfaceStyle}
            className={cn(
              "flex grow flex-col gap-y-5 overflow-y-auto border-r shadow-sm bg-card px-2 xl:px-4",
              onBrand ? "border-[var(--brand-border-color)]" : "border-border",
            )}
          >
              <div className="hidden xl:flex items-center text-xl font-medium h-20 flex-row shrink-0 w-full">
                <Link to={"/"} className="w-full">
                  <SpaceLogo partner={currentSpace.partner} className="h-6 w-[102px]" />
                </Link>
              </div>
              <div className={"flex xl:hidden justify-center shrink-0 items-center px-2 h-20"}>
                <Link to={"/"}>
                  <SpaceLogo partner={currentSpace.partner} className="h-6" variant="rail" />
                </Link>
              </div>
              <nav className="flex flex-1 flex-col">
                <div role="list" className="flex flex-1 flex-col gap-y-2 list-none">
                  <div role="list" className="list-none flex flex-col gap-y-2">
                    {navigation
                      .filter((x) => x.available(currentSpace, user))
                      .map((item, idx) =>
                        item.separator ? (
                          <Separator
                            key={`sep-desktop-${idx}`}
                            className={cn(
                              "my-2",
                              onBrand ? "bg-[var(--brand-border-color)]" : "bg-border",
                            )}
                          />
                        ) : (
                          <MenuItem
                            item={item}
                            key={item.href || item.name}
                            onBrand={onBrand}
                            brandHoverBg={brandHoverBg}
                            brandTextColor={brandTextColor}
                          />
                        ),
                      )}
                  </div>
                </div>
                <div className="mt-auto">
                  {authenticated && !spaces.find((x) => x.id === currentSpace.id) && (
                    <>
                      {/* Show card in xl mode */}
                      <Card className="light hidden xl:block">
                        <CardHeader className="p-3">
                          <CardTitle className="flex flex-row items-center gap-1 text-warning">
                            <MessageCircleWarningIcon className="h-5" />
                            <span>
                              <Trans>Not a member</Trans>
                            </span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 pt-0">
                          <p className="text-muted-foreground text-sm">
                            {!!currentSpace.configuration?.public ? (
                              <Trans>
                                Switch to your existing space below, or{" "}
                                <a
                                  href="#"
                                  onClick={handleJoinSpace}
                                  className="text-foreground underline hover:text-foreground/80"
                                >
                                  join {currentSpace.name}
                                </a>
                                .
                              </Trans>
                            ) : (
                              <Trans>Switch to your existing space below.</Trans>
                            )}
                          </p>
                        </CardContent>
                      </Card>

                      {/* Show info icon with popover in small mode */}
                      <div className="xl:hidden flex justify-center">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-9 w-9">
                              <MessageCircleWarningIcon className="h-5 w-5 text-warning" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80" side="right">
                            <div className="flex flex-col gap-2">
                              <div className="flex flex-row items-center gap-1 text-warning font-medium">
                                <MessageCircleWarningIcon className="h-5" />
                                <span>
                                  <Trans>Not a member</Trans>
                                </span>
                              </div>
                              <p className="text-muted-foreground text-sm">
                                {!!currentSpace.configuration?.public ? (
                                  <Trans>
                                    Switch to your existing space below, or{" "}
                                    <a
                                      href="#"
                                      onClick={handleJoinSpace}
                                      className="text-foreground underline hover:text-foreground/80"
                                    >
                                      join {currentSpace.name}
                                    </a>
                                    .
                                  </Trans>
                                ) : (
                                  <Trans>Switch to your existing space below.</Trans>
                                )}
                              </p>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </>
                  )}
                  {authenticated && (spaces.length > 1 || !spaces.find((x) => x.id === currentSpace.id)) ? (
                    <DropdownMenu>
                      <TooltipProvider>
                        <Tooltip>
                          <div>
                            <DropdownMenuTrigger asChild>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className={cn(
                                    "py-2 flex w-full items-center gap-x-2 px-2.5 py-2 typography-detail rounded-md my-4 h-auto",
                                    onBrand ? "bg-[var(--brand-hover-bg)]" : "bg-slate-900",
                                  )}
                                >
                                  <img
                                    className="shrink-0 h-4 w-4 rounded-xs object-contain"
                                    src={currentSpace.icon}
                                    alt={currentSpace.name}
                                  />
                                  <span className="sr-only text-left">
                                    <Trans>Change space</Trans>
                                  </span>
                                  <div className="hidden xl:block line-clamp-1 grow w-full text-left">
                                    {currentSpace.name}
                                  </div>
                                  <ChevronUpDownIcon className={"h-8 w-8"} />
                                </Button>
                              </TooltipTrigger>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-56  max-h-[300px] md:max-h-[400px] lg:max-h-[600px] overflow-auto">
                              <DropdownMenuLabel>
                                <Trans>Spaces</Trans>
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {spaces.map((space) => {
                                return (
                                  <DropdownMenuCheckboxItem
                                    key={space.uniqueName}
                                    checked={space.uniqueName === currentSpace.uniqueName}
                                    className="flex justify-between"
                                    onCheckedChange={() => {
                                      window.location.href = spaceUrl(config.appUrl, currentSpace.uniqueName, space.uniqueName);
                                    }}
                                  >
                                    <span>{space.name}</span>
                                    <img className="h-6 w-6 rounded-xs bg-gray-50 object-contain" src={space.icon} alt={space.name} />
                                  </DropdownMenuCheckboxItem>
                                );
                              })}
                            </DropdownMenuContent>
                          </div>
                          <TooltipContent>
                            <span className="typography-detail">
                              <Trans>Change space</Trans>
                            </span>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </DropdownMenu>
                  ) : (
                    <div className="flex justify-center w-full">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-x-2 px-2.5 py-2 typography-detail rounded-md my-4 h-auto max-w-[90%]">
                              <img
                                className="shrink-0 h-4 w-4 rounded-xs object-contain"
                                src={currentSpace.icon}
                                alt={currentSpace.name}
                              />
                              <div className="hidden xl:block line-clamp-1 grow w-full text-left">
                                {currentSpace.name}
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span className="typography-detail">{currentSpace.name}</span>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>
              </nav>
            </div>
          </div>
        </>
      )}

      {/* Main content area. The shell above clips at the screen height, so main
          scrolls itself: pages without a PageContainer would otherwise be cut
          off on a phone (FOX-6081). */}
      <main className="relative w-full h-[calc(100dvh-8rem)] md:h-full grow overflow-y-auto" ref={setContainer}>
        {children}
      </main>

      {/* NPS Survey Container */}
      <NPSSurveyContainer />

      <DiscountCodeDialog
        open={discountDialogOpen}
        onOpenChange={setDiscountDialogOpen}
        referralCode={referralCode || ""}
      />
      <ReportBugDialog
        open={reportBugDialogOpen}
        onOpenChange={setReportBugDialogOpen}
        mindspaceId={currentSpace.id}
        environment={config.environment}
      />
    </div>
  );
}
function MenuItem({
  item,
  className,
  closeSidebar,
  onBrand,
  brandHoverBg,
  brandTextColor,
}: {
  item: NavigationItem;
  className?: string;
  closeSidebar?: () => any;
  /** True when the sidebar background is a partner brand colour — use a
   * darker shade of the brand colour for hover/active states instead of the
   * default violet or generic grey pills. */
  onBrand?: boolean;
  brandHoverBg?: string;
  brandTextColor?: string;
}) {
  const location = useLocation();
  const isClient = isFrontend();
  const [open, setOpen] = useState(false);
  const { t } = useLingui();

  const handleMouseEnter = () => {
    if (isClient) {
      setOpen(true);
    }
  };
  const place: NavPlace = closeSidebar ? "drawer" : "rail";
  const introOpen = item.intro?.openAt === place;

  const handleMouseLeave = () => {
    if (isClient) {
      setOpen(false);
    }
  };

  const Icon = item.icon;
  const id = `nav_${item.name.toLowerCase()}`;
  const introPanelId = `${id}_intro_${place}`;
  const displayLabel = item.label ? t(item.label) : item.name;
  const spokenLabel = item.spokenLabel ? t(item.spokenLabel) : displayLabel;
  const badgeLabel = item.badge ? t(item.badge) : null;

  const content = React.useMemo(() => {
    if (item.href) {
      return (
        <Link
          to={item.href}
          id={id}
          reloadDocument={item.reloadDocument}
          target={item.newTab ? "_blank" : undefined}
          rel={item.newTab ? "noopener" : undefined}
          className="hover:!no-underline !gap-0"
        >
          <Icon className={cn("size-4 md:size-5 shrink-0")} aria-hidden="true" />
          {item.newTab || badgeLabel ? (
            // The marker wraps under the label in a narrow sidebar rather than
            // splitting the label mid-word.
            <span className="flex md:hidden xl:flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 text-left whitespace-normal">
              <span className="typography-small leading-tight min-w-0 break-words">
                {displayLabel}
                {item.newTab && (
                  <ArrowUpRightIcon className="inline size-3 ml-1 align-baseline" aria-hidden="true" />
                )}
              </span>
              {badgeLabel && (
                <Badge variant="secondary" className="shrink-0" aria-hidden="true">
                  {badgeLabel}
                </Badge>
              )}
            </span>
          ) : (
            <span className="block md:hidden xl:block typography-small text-left leading-tight min-w-0 whitespace-normal break-words">{displayLabel}</span>
          )}
        </Link>
      );
    }
    return (
      <span id={id}>
        <Icon className={cn("h-4 w-4 md:h-5 md:w-5 shrink-0")} aria-hidden="true" />
        <span className="block md:hidden xl:block typography-small text-left leading-tight min-w-0 whitespace-normal break-words">{displayLabel}</span>
      </span>
    );
  }, [item.href, item.action, item.reloadDocument, item.newTab, displayLabel, badgeLabel]);
  const navItem = (
    <Popover open={isClient ? open && !introOpen : false} onOpenChange={setOpen}>
      <PopoverTrigger onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} asChild>
        <Button
          variant="ghost"
          className={cn(
            "relative min-h-10 w-full focus-visible:ring-0 focus-visible:ring-offset-0" +
              " justify-start md:justify-center xl:justify-start rounded-md flex space-x-2 items-center px-3 py-1.5",
            // Partner sidebars use the branded hover/active treatment; the
            // default theme keeps the original Mindstone text-only active state.
            onBrand
              ? "hover:bg-[var(--brand-nav-hover-bg)] dark:hover:bg-[var(--brand-nav-hover-bg)]"
              : "xl:hover:bg-transparent",
            item.current &&
              item.current(location.pathname) &&
              (onBrand
                ? "text-primary font-semibold"
                : "text-violet-400 hover:text-violet-400"),
            onBrand ? "hover:text-current dark:hover:text-current" : undefined,
            "xl:hover:underline xl:group",
            className,
          )}
          onClick={(event: React.MouseEvent) => {
            // A5: a plain click (or Enter) on a pending intro opens it instead
            // of the link and keeps the drawer open under it. Cmd/Ctrl/Shift
            // clicks are the person choosing a tab themselves: left alone.
            const plain =
              event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
            // A second tap while it is open (on touch, Radix dismisses only
            // after the click) is not another entry: leave it to Radix.
            if (introOpen) {
              event.preventDefault();
              return;
            }
            if (item.intro?.pending && plain) {
              event.preventDefault();
              item.intro.show(place);
              item.action?.({ popupShown: true });
              return;
            }
            if (closeSidebar) closeSidebar();
            item.action?.({ popupShown: false });
          }}
          onAuxClick={(event: React.MouseEvent) => {
            // A middle-click opens the link in a new tab without a click event:
            // still an entry. Not a right-click, nor an item with no link. An
            // open intro has already closed on the pointerdown (a Not now).
            if (event.button !== 1 || !item.href) return;
            item.action?.({ popupShown: false });
          }}
          asChild={!!item.href}
          aria-label={spokenLabel}
          // While the intro is open the link controls it, not the hover label.
          {...(introOpen ? { "aria-expanded": true, "aria-controls": introPanelId } : {})}
        >
          {content}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        onMouseLeave={handleMouseLeave}
        // The intro opening closes this hover label; its usual focus hand-back
        // to the item would land outside the intro and dismiss it.
        onCloseAutoFocus={introOpen ? (event) => event.preventDefault() : undefined}
        style={onBrand ? { backgroundColor: brandHoverBg, color: brandTextColor } : undefined}
        className={cn(
          "hidden md:flex xl:hidden dark bg-accent items-center text-foreground py-1 w-fit rounded-none border-none rounded-r-md -mx-2 h-10",
          item.current &&
            item.current(location.pathname) &&
            (onBrand ? "text-current font-semibold" : "text-primary font-semibold"),
          className,
        )}
      >
        <span className="typography-small">{displayLabel}</span>
      </PopoverContent>
    </Popover>
  );
  return item.intro ? item.intro.wrap(place, navItem, introPanelId) : navItem;
}
